import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth, ROLES_COMPTA } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Plus, Receipt, Loader2, Eye, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, Trash2, Send, ThumbsUp, Download,
  BarChart3, FileText, Euro, AlertCircle, Pencil, Unlock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

// ── Constantes ────────────────────────────────────────────────────────────────

const TVA_RATES = [0, 5.5, 10, 20];
const TICKET_TYPES = [
  { value: "repas", label: "Repas" },
  { value: "transport", label: "Transport" },
  { value: "hébergement", label: "Hébergement" },
  { value: "fournitures", label: "Fournitures" },
  { value: "autre", label: "Autre" },
];

const STATUS_CONFIG = {
  draft:             { label: "Brouillon",           badgeClass: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
  submitted:         { label: "Soumise",             badgeClass: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  manager_approved:  { label: "Approuvée manager",   badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  approved:          { label: "Approuvée",           badgeClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  rejected:          { label: "Rejetée",             badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
};

const fmtEur = (v) => `${Number(v ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPeriod = (p) => {
  if (!p) return "—";
  try { return format(new Date(p + "-01"), "MMMM yyyy", { locale: fr }); }
  catch { return p; }
};

// ── StatusBadge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badgeClass}`}>{cfg.label}</span>;
};

// ── NDFItemForm ───────────────────────────────────────────────────────────────
const NDFItemForm = ({ ndfId, item, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    description: item?.description || "",
    affaire: item?.affaire || "",
    montant_ht: item?.montant_ht ?? "",
    tva_rate: item?.tva_rate ?? 20,
    ticket_type: item?.ticket_type || "autre",
    photo_data: item?.photo_data || null,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const montant_ttc = form.montant_ht ? +(parseFloat(form.montant_ht) * (1 + form.tva_rate / 100)).toFixed(2) : 0;

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => set("photo_data", ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.description || form.montant_ht === "") return toast.error("Description et montant requis");
    setSaving(true);
    try {
      const payload = { ...form, montant_ht: parseFloat(form.montant_ht), tva_rate: parseFloat(form.tva_rate) };
      if (item?.id) {
        await axios.put(`${API}/ndf/${ndfId}/items/${item.id}`, payload);
      } else {
        await axios.post(`${API}/ndf/${ndfId}/items`, payload);
      }
      toast.success(item?.id ? "Ligne mise à jour" : "Ligne ajoutée");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-gray-50 dark:bg-[#112240]/60 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">Description *</Label>
          <Input value={form.description} onChange={(e) => set("description", e.target.value)}
            placeholder="Ex: Déjeuner client ACME" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white" />
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">Affaire / Projet</Label>
          <Input value={form.affaire} onChange={(e) => set("affaire", e.target.value)}
            placeholder="Optionnel" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">Montant HT (€) *</Label>
          <Input type="number" step="0.01" min="0" value={form.montant_ht}
            onChange={(e) => set("montant_ht", e.target.value)}
            className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white" />
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">TVA (%)</Label>
          <Select value={String(form.tva_rate)} onValueChange={(v) => set("tva_rate", parseFloat(v))}>
            <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TVA_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">TTC calculé</Label>
          <div className="h-9 flex items-center px-3 border rounded-md bg-gray-100 dark:bg-[#060E1C] dark:border-[#1A3050] text-sm font-medium dark:text-[#4CAF70]">
            {fmtEur(montant_ttc)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">Type de ticket</Label>
          <Select value={form.ticket_type} onValueChange={(v) => set("ticket_type", v)}>
            <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TICKET_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300 text-xs">Photo du ticket</Label>
          <Input type="file" accept="image/*" onChange={handlePhoto}
            className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white text-xs" />
        </div>
      </div>
      {form.photo_data && (
        <img src={form.photo_data} alt="ticket" className="h-20 rounded border dark:border-[#1A3050] object-cover" />
      )}
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}
          className="dark:border-[#1A3050] dark:text-[#7EA8CE] dark:hover:bg-[#112240]">
          Annuler
        </Button>
        <Button size="sm" disabled={saving} onClick={handleSave}
          className="bg-[#1A4D2E] hover:bg-[#143D24] text-white">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : (item?.id ? "Mettre à jour" : "Ajouter")}
        </Button>
      </div>
    </div>
  );
};

// ── NDFSheet ──────────────────────────────────────────────────────────────────
const NDFSheet = ({ ndf, open, onClose, onRefresh, currentUser }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [acting, setActing] = useState(false);

  const isOwner = detail?.user_id === currentUser?.id;
  const isDraft = detail?.status === "draft";
  const canEdit = isOwner && isDraft;
  const isCompta = ROLES_COMPTA.includes(currentUser?.role);
  const isManager = currentUser?.role === "manager" || isCompta;

  const fetchDetail = useCallback(async () => {
    if (!ndf?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ndf/${ndf.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Erreur chargement NDF");
    } finally {
      setLoading(false);
    }
  }, [ndf?.id]);

  useEffect(() => { if (open) fetchDetail(); }, [open, fetchDetail]);

  const action = async (endpoint, payload = {}) => {
    setActing(true);
    try {
      await axios.post(`${API}/ndf/${ndf.id}/${endpoint}`, payload);
      toast.success("Action effectuée");
      fetchDetail();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setActing(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/ndf/${ndf.id}/items/${itemId}`);
      toast.success("Ligne supprimée");
      fetchDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto dark:bg-[#0B1628] dark:border-[#1A3050] p-0">
        {loading || !detail ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
          </div>
        ) : (
          <>
            <div className="p-6 border-b dark:border-[#1A3050] bg-gradient-to-r from-[#1A4D2E]/5 to-transparent dark:from-[#1A4D2E]/20">
              <SheetHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <SheetTitle className="text-xl font-bold dark:text-white">{detail.title}</SheetTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {detail.user_name} · {fmtPeriod(detail.period)}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <StatusBadge status={detail.status} />
                      <span className="text-sm font-semibold text-[#1A4D2E] dark:text-[#4CAF70]">
                        {fmtEur(detail.total_ttc)} TTC
                      </span>
                      <span className="text-xs text-gray-400">(HT: {fmtEur(detail.total_ht)})</span>
                    </div>
                  </div>
                </div>
              </SheetHeader>
            </div>

            <div className="p-6 space-y-5">
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {canEdit && (
                  <Button size="sm" onClick={() => action("submit")} disabled={acting || detail.items.length === 0}
                    className="bg-[#1A4D2E] hover:bg-[#143D24] text-white gap-1">
                    <Send className="w-3 h-3" /> Soumettre
                  </Button>
                )}
                {detail.status === "submitted" && isManager && !isOwner && (
                  <Button size="sm" onClick={() => action("manager-approve")} disabled={acting}
                    className="bg-amber-500 hover:bg-amber-600 text-white gap-1">
                    <ThumbsUp className="w-3 h-3" /> Approuver (manager)
                  </Button>
                )}
                {(detail.status === "submitted" || detail.status === "manager_approved") && isCompta && (
                  <Button size="sm" onClick={() => action("approve")} disabled={acting}
                    className="bg-green-600 hover:bg-green-700 text-white gap-1">
                    <CheckCircle className="w-3 h-3" /> Validation finale
                  </Button>
                )}
                {(detail.status === "submitted" || detail.status === "manager_approved") && isManager && (
                  <Button size="sm" variant="outline" onClick={() => setShowRejectForm(!showRejectForm)}
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 gap-1">
                    <XCircle className="w-3 h-3" /> Rejeter
                  </Button>
                )}
                {(detail.status === "submitted" || detail.status === "manager_approved" || detail.status === "rejected") && isManager && (
                  <Button size="sm" variant="outline" onClick={() => action("unlock")} disabled={acting}
                    className="dark:border-[#1A3050] dark:text-[#7EA8CE] gap-1">
                    <Unlock className="w-3 h-3" /> Déverrouiller
                  </Button>
                )}
              </div>

              {showRejectForm && (
                <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-red-50 dark:bg-red-900/10 space-y-2">
                  <Label className="text-sm font-medium text-red-700 dark:text-red-400">Raison du rejet *</Label>
                  <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Expliquez la raison du rejet..." rows={2}
                    className="dark:bg-[#112240] dark:border-red-700 dark:text-white" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowRejectForm(false)}
                      className="dark:border-[#1A3050] dark:text-[#7EA8CE]">Annuler</Button>
                    <Button size="sm" disabled={!rejectReason || acting}
                      onClick={() => { action("reject", { reason: rejectReason }); setShowRejectForm(false); }}
                      className="bg-red-600 hover:bg-red-700 text-white">Confirmer le rejet</Button>
                  </div>
                </div>
              )}

              {detail.rejection_reason && (
                <div className="border border-red-200 dark:border-red-700 rounded-xl p-3 bg-red-50 dark:bg-red-900/10">
                  <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Raison du rejet</p>
                  <p className="text-sm text-red-700 dark:text-red-300">{detail.rejection_reason}</p>
                </div>
              )}

              {/* Lignes de frais */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm dark:text-[#D6E4F5]">Lignes de frais ({detail.items.length})</h3>
                  {canEdit && !showItemForm && (
                    <Button size="sm" variant="outline" onClick={() => { setShowItemForm(true); setEditingItem(null); }}
                      className="dark:border-[#1A3050] dark:text-[#7EA8CE] dark:hover:bg-[#112240] gap-1">
                      <Plus className="w-3 h-3" /> Ajouter une ligne
                    </Button>
                  )}
                </div>

                {showItemForm && !editingItem && (
                  <div className="mb-3">
                    <NDFItemForm
                      ndfId={detail.id}
                      item={null}
                      onSaved={() => { setShowItemForm(false); fetchDetail(); }}
                      onCancel={() => setShowItemForm(false)}
                    />
                  </div>
                )}

                {detail.items.length === 0 && !showItemForm && (
                  <div className="text-center py-8 border dark:border-[#1A3050] rounded-xl">
                    <Receipt className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-[#3D607E]" />
                    <p className="text-sm text-gray-400 dark:text-[#3D607E]">Aucune ligne de frais</p>
                  </div>
                )}

                <div className="space-y-2">
                  {detail.items.map((item) => (
                    <div key={item.id}>
                      {editingItem?.id === item.id ? (
                        <NDFItemForm
                          ndfId={detail.id}
                          item={item}
                          onSaved={() => { setEditingItem(null); fetchDetail(); }}
                          onCancel={() => setEditingItem(null)}
                        />
                      ) : (
                        <div className="border dark:border-[#1A3050] rounded-lg p-3 flex items-start gap-3">
                          {item.photo_data && (
                            <img src={item.photo_data} alt="ticket" className="w-12 h-12 object-cover rounded shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium dark:text-[#D6E4F5] truncate">{item.description}</p>
                            {item.affaire && <p className="text-xs text-gray-400 dark:text-[#3D607E]">{item.affaire}</p>}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs bg-gray-100 dark:bg-[#112240] px-1.5 py-0.5 rounded">
                                {TICKET_TYPES.find(t => t.value === item.ticket_type)?.label || item.ticket_type}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-[#7EA8CE]">TVA {item.tva_rate}%</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-semibold dark:text-[#4CAF70]">{fmtEur(item.montant_ttc)}</p>
                            <p className="text-xs text-gray-400">HT: {fmtEur(item.montant_ht)}</p>
                          </div>
                          {canEdit && (
                            <div className="flex gap-1 shrink-0">
                              <Button variant="ghost" size="icon" className="w-7 h-7 text-[#1A4D2E] dark:text-[#4B8BF4]"
                                onClick={() => { setEditingItem(item); setShowItemForm(false); }}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="w-7 h-7 text-red-400 hover:text-red-600"
                                onClick={() => handleDeleteItem(item.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Totaux */}
              {detail.items.length > 0 && (
                <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-gray-50 dark:bg-[#112240]/60">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500 dark:text-[#7EA8CE]">Total HT</span>
                    <span className="font-medium dark:text-[#D6E4F5]">{fmtEur(detail.total_ht)}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold border-t dark:border-[#1A3050] pt-2 mt-2">
                    <span className="dark:text-[#D6E4F5]">Total TTC</span>
                    <span className="text-[#1A4D2E] dark:text-[#4CAF70]">{fmtEur(detail.total_ttc)}</span>
                  </div>
                </div>
              )}

              {/* Historique */}
              <div className="border dark:border-[#1A3050] rounded-xl p-4 space-y-2">
                <h3 className="text-xs font-semibold text-gray-400 dark:text-[#3D607E] uppercase tracking-wide">Historique</h3>
                <div className="space-y-1 text-xs text-gray-500 dark:text-[#7EA8CE]">
                  <p>Créée le {format(new Date(detail.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}</p>
                  {detail.submitted_at && <p>Soumise le {format(new Date(detail.submitted_at), "dd/MM/yyyy HH:mm", { locale: fr })}</p>}
                  {detail.manager_approved_at && <p>Approuvée manager le {format(new Date(detail.manager_approved_at), "dd/MM/yyyy HH:mm", { locale: fr })}</p>}
                  {detail.approved_at && <p>Approbation finale le {format(new Date(detail.approved_at), "dd/MM/yyyy HH:mm", { locale: fr })}</p>}
                  {detail.rejected_at && <p>Rejetée le {format(new Date(detail.rejected_at), "dd/MM/yyyy HH:mm", { locale: fr })}</p>}
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ── NDFCreateSheet ────────────────────────────────────────────────────────────
const NDFCreateSheet = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: "", period: "" });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.title || !form.period) return toast.error("Titre et période requis");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ndf`, form);
      toast.success("NDF créée");
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="sm:max-w-md dark:bg-[#0B1628] dark:border-[#1A3050]">
        <SheetHeader>
          <SheetTitle className="dark:text-white">Nouvelle note de frais</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          <div className="space-y-1">
            <Label className="dark:text-gray-300">Titre *</Label>
            <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Ex: Déplacement client Paris - Jan 2026"
              className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white" />
          </div>
          <div className="space-y-1">
            <Label className="dark:text-gray-300">Période *</Label>
            <Input type="month" value={form.period} onChange={(e) => setForm(p => ({ ...p, period: e.target.value }))}
              className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-white" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 dark:border-[#1A3050] dark:text-[#7EA8CE]">
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── NDFTable ──────────────────────────────────────────────────────────────────
const NDFTable = ({ ndfs, onView, currentUser }) => {
  const isCompta = ROLES_COMPTA.includes(currentUser?.role);
  const isManager = currentUser?.role === "manager";

  const handleAction = async (e, ndfId, action, payload = {}) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/ndf/${ndfId}/${action}`, payload);
      toast.success("Action effectuée");
      onView(null); // refresh signal
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <>
      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {ndfs.map((ndf) => (
          <div key={ndf.id} onClick={() => onView(ndf)}
            className="border dark:border-[#1A3050] rounded-xl p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#112240]/50 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="font-medium text-sm dark:text-[#D6E4F5] truncate">{ndf.title}</p>
                {(isCompta || isManager) && (
                  <p className="text-xs text-gray-400 dark:text-[#3D607E]"><ClickableUser userId={ndf.user_id}>{ndf.user_name}</ClickableUser></p>
                )}
              </div>
              <StatusBadge status={ndf.status} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500 dark:text-[#7EA8CE]">{fmtPeriod(ndf.period)}</span>
              <span className="font-semibold text-[#1A4D2E] dark:text-[#4CAF70]">{fmtEur(ndf.total_ttc)}</span>
            </div>
            {ndf.submitted_at && (
              <p className="text-xs text-gray-400 mt-1">
                Soumise le {format(new Date(ndf.submitted_at), "dd/MM/yyyy", { locale: fr })}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b dark:border-[#1A3050] text-left">
              {(isCompta || isManager) && <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE] pr-4">Collaborateur</th>}
              <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE] pr-4">Titre</th>
              <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE] pr-4">Période</th>
              <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE] pr-4 text-right">Total TTC</th>
              <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE] pr-4">Statut</th>
              <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE] pr-4">Soumise le</th>
              <th className="pb-3 font-medium text-gray-500 dark:text-[#7EA8CE]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-[#1A3050]">
            {ndfs.map((ndf) => (
              <tr key={ndf.id} className="hover:bg-gray-50 dark:hover:bg-[#112240]/30 cursor-pointer transition-colors"
                onClick={() => onView(ndf)}>
                {(isCompta || isManager) && (
                  <td className="py-3 pr-4 dark:text-[#D6E4F5]"><ClickableUser userId={ndf.user_id}>{ndf.user_name}</ClickableUser></td>
                )}
                <td className="py-3 pr-4 dark:text-[#D6E4F5] max-w-[200px] truncate">{ndf.title}</td>
                <td className="py-3 pr-4 text-gray-500 dark:text-[#7EA8CE]">{fmtPeriod(ndf.period)}</td>
                <td className="py-3 pr-4 text-right font-medium text-[#1A4D2E] dark:text-[#4CAF70]">{fmtEur(ndf.total_ttc)}</td>
                <td className="py-3 pr-4"><StatusBadge status={ndf.status} /></td>
                <td className="py-3 pr-4 text-gray-500 dark:text-[#7EA8CE] text-xs">
                  {ndf.submitted_at ? format(new Date(ndf.submitted_at), "dd/MM/yyyy", { locale: fr }) : "—"}
                </td>
                <td className="py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-[#1A4D2E] dark:text-[#4B8BF4]"
                      onClick={() => onView(ndf)} title="Voir">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    {ndf.status === "submitted" && (isCompta || isManager) && ndf.user_id !== currentUser?.id && (
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-amber-500"
                        onClick={(e) => handleAction(e, ndf.id, "manager-approve")} title="Approuver manager">
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {(ndf.status === "submitted" || ndf.status === "manager_approved") && isCompta && (
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-green-600"
                        onClick={(e) => handleAction(e, ndf.id, "approve")} title="Validation finale">
                        <CheckCircle className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── NDFPage ───────────────────────────────────────────────────────────────────
export default function NDFPage() {
  const { user } = useAuth();
  const [ndfs, setNdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [viewNDF, setViewNDF] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);

  const isCompta = ROLES_COMPTA.includes(user?.role);
  const isManager = user?.role === "manager";

  const fetchNDFs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ndf`);
      setNdfs(r.data);
    } catch (e) {
      toast.error("Erreur chargement NDF");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    if (!isCompta) return;
    try {
      const r = await axios.get(`${API}/ndf/stats/global`);
      setStats(r.data);
    } catch {}
  }, [isCompta]);

  useEffect(() => {
    fetchNDFs();
    fetchStats();
  }, [fetchNDFs, fetchStats, refreshToken]);

  const refresh = () => setRefreshToken(t => t + 1);

  const myNDFs = useMemo(() => ndfs.filter(n => n.user_id === user?.id), [ndfs, user]);
  const teamNDFs = useMemo(() => ndfs.filter(n => n.user_id !== user?.id), [ndfs, user]);

  const filterNDFs = (list) => list.filter(n => {
    if (statusFilter !== "all" && n.status !== statusFilter) return false;
    if (searchQuery && !n.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !n.user_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const exportCSV = () => {
    const rows = [
      ["Collaborateur", "Titre", "Période", "Total HT", "Total TTC", "Statut", "Soumise le"],
      ...ndfs.map(n => [
        n.user_name, n.title, n.period,
        n.total_ht.toFixed(2), n.total_ttc.toFixed(2),
        STATUS_CONFIG[n.status]?.label || n.status,
        n.submitted_at ? format(new Date(n.submitted_at), "dd/MM/yyyy") : "",
      ]),
    ];
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ndf_export_${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleViewOrRefresh = (ndf) => {
    if (ndf === null) { refresh(); return; }
    setViewNDF(ndf);
  };

  // ── KPI Cards ────────────────────────────────────────────────────────────
  const kpiCards = isCompta && stats ? [
    { label: "Total TTC ce mois", value: fmtEur(stats.total_month_ttc), icon: Euro, color: "text-[#1A4D2E] dark:text-[#4CAF70]" },
    { label: "En attente validation finale", value: stats.pending_final, icon: Clock, color: "text-amber-600 dark:text-amber-400" },
    { label: "Approuvées ce mois", value: stats.approved_month, icon: CheckCircle, color: "text-green-600 dark:text-green-400" },
    { label: "Total NDF", value: stats.total_ndfs, icon: FileText, color: "text-blue-600 dark:text-blue-400" },
  ] : !isCompta ? [
    { label: "Mes NDF", value: myNDFs.length, icon: FileText, color: "text-blue-600 dark:text-blue-400" },
    { label: "Soumises", value: myNDFs.filter(n => n.status === "submitted").length, icon: Send, color: "text-amber-600 dark:text-amber-400" },
    { label: "Approuvées", value: myNDFs.filter(n => n.status === "approved").length, icon: CheckCircle, color: "text-green-600 dark:text-green-400" },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold dark:text-white">Notes de Frais</h1>
          <p className="text-sm text-gray-500 dark:text-[#7EA8CE] mt-0.5">
            {isCompta ? "Gestion et validation des notes de frais" : "Gérez vos demandes de remboursement"}
          </p>
        </div>
        <div className="flex gap-2">
          {isCompta && (
            <Button variant="outline" onClick={exportCSV}
              className="dark:border-[#1A3050] dark:text-[#7EA8CE] dark:hover:bg-[#112240] gap-1">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)}
            className="bg-[#1A4D2E] hover:bg-[#143D24] text-white gap-1">
            <Plus className="w-4 h-4" /> Nouvelle NDF
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {kpiCards.length > 0 && (
        <div className={`grid grid-cols-2 ${isCompta ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-3`}>
          {kpiCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#0B1628]">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${kpi.color}`} />
                  <span className="text-xs text-gray-500 dark:text-[#7EA8CE]">{kpi.label}</span>
                </div>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* BarChart (compta) */}
      {isCompta && stats?.monthly_data?.length > 0 && (
        <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#0B1628]">
          <h2 className="text-sm font-semibold dark:text-[#D6E4F5] mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
            Montants NDF par mois
          </h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.monthly_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A3050" />
              <XAxis dataKey="period" tick={{ fill: "#7EA8CE", fontSize: 11 }} />
              <YAxis tick={{ fill: "#7EA8CE", fontSize: 11 }} tickFormatter={(v) => `${v} €`} />
              <Tooltip formatter={(v) => fmtEur(v)} labelStyle={{ color: "#D6E4F5" }}
                contentStyle={{ backgroundColor: "#0B1628", border: "1px solid #1A3050" }} />
              <Legend wrapperStyle={{ color: "#7EA8CE", fontSize: 12 }} />
              <Bar dataKey="total_ht" name="HT" fill="#1A4D2E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_ttc" name="TTC" fill="#E07A5F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="sm:max-w-xs dark:bg-[#0B1628] dark:border-[#1A3050] dark:text-white dark:placeholder-[#3D607E]" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-48 dark:bg-[#0B1628] dark:border-[#1A3050] dark:text-white">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      ) : isManager ? (
        <Tabs defaultValue="team">
          <TabsList className="dark:bg-[#112240]">
            <TabsTrigger value="team" className="dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
              Mon équipe ({teamNDFs.length})
            </TabsTrigger>
            <TabsTrigger value="mine" className="dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
              Mes NDF ({myNDFs.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="team" className="mt-4">
            {filterNDFs(teamNDFs).length === 0 ? (
              <EmptyState />
            ) : (
              <NDFTable ndfs={filterNDFs(teamNDFs)} onView={handleViewOrRefresh} currentUser={user} />
            )}
          </TabsContent>
          <TabsContent value="mine" className="mt-4">
            {filterNDFs(myNDFs).length === 0 ? (
              <EmptyState />
            ) : (
              <NDFTable ndfs={filterNDFs(myNDFs)} onView={handleViewOrRefresh} currentUser={user} />
            )}
          </TabsContent>
        </Tabs>
      ) : (
        filterNDFs(ndfs).length === 0 ? <EmptyState /> :
          <NDFTable ndfs={filterNDFs(ndfs)} onView={handleViewOrRefresh} currentUser={user} />
      )}

      {/* Modals */}
      <NDFCreateSheet
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(newNdf) => { setShowCreate(false); setViewNDF(newNdf); refresh(); }}
      />

      {viewNDF && (
        <NDFSheet
          ndf={viewNDF}
          open={!!viewNDF}
          onClose={() => setViewNDF(null)}
          onRefresh={refresh}
          currentUser={user}
        />
      )}
    </div>
  );
}

const EmptyState = () => (
  <div className="text-center py-16 border dark:border-[#1A3050] rounded-xl bg-white dark:bg-[#0B1628]">
    <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-[#3D607E]" />
    <p className="text-gray-500 dark:text-[#7EA8CE]">Aucune note de frais</p>
  </div>
);
